import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useTenant } from "@/context/tenant-context";
import { skillsApi } from "@/api/skills";
import { queryKeys } from "@/lib/query-keys";
import { Globe, Zap, Plus, Trash2, User, Workflow } from "lucide-react";
import type { TenantSkillScope } from "@/api/skills";

interface TenantSkill {
  id: string;
  companyId: string;
  catalogueSkillId: string | null;
  name: string;
  enabled: boolean;
  scope: TenantSkillScope;
}

const SCOPE_META: Record<
  TenantSkillScope,
  { label: string; icon: typeof Globe; title: string }
> = {
  agent: {
    label: "agent",
    icon: User,
    title:
      "Loaded only by agents that have this skill assigned. It sits in those agents' system prompt on every turn.",
  },
  global: {
    label: "global",
    icon: Globe,
    title:
      "Loaded by every agent in this tenant. It sits in every agent's system prompt on every turn — use sparingly, this is the most expensive scope.",
  },
  workflow: {
    label: "workflow only",
    icon: Workflow,
    title:
      "Never loaded into any agent's chat. Attach it to a workflow agent node; it is handed to the agent only while that step runs, so it costs nothing the rest of the time.",
  },
};

export function TenantSkillsSettings() {
  const { tenantId } = useTenant();
  const queryClient = useQueryClient();
  const [selectedSkill, setSelectedSkill] = useState("");

  const { data: skillCatalogue = [] } = useQuery({
    queryKey: queryKeys.skillCatalogue.all,
    queryFn: skillsApi.listCatalogue,
  });
  const { data: tenantSkills = [] } = useQuery({
    queryKey: queryKeys.tenantSkills.byCompany(tenantId),
    queryFn: () => skillsApi.listTenantSkills(tenantId) as Promise<TenantSkill[]>,
  });

  const assignedSkillIds = new Set(tenantSkills.map((t) => t.catalogueSkillId));
  const availableSkills = skillCatalogue.filter((c) => !assignedSkillIds.has(c.id));

  const assignSkill = useMutation({
    mutationFn: (catalogueSkillId: string) => {
      const cat = skillCatalogue.find((c) => c.id === catalogueSkillId);
      return skillsApi.assignTenantSkill(tenantId, {
        companyId: tenantId,
        catalogueSkillId,
        name: cat?.name || catalogueSkillId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tenantSkills.byCompany(tenantId) });
      setSelectedSkill("");
    },
  });

  const removeSkill = useMutation({
    mutationFn: (id: string) => skillsApi.removeTenantSkill(tenantId, id),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: queryKeys.tenantSkills.byCompany(tenantId) }),
  });

  /**
   * Set the skill's scope. The PATCH triggers an eager fan-out syncSkills
   * across the tenant on the server, in every direction:
   *   → global   every agent materialises the SKILL.md
   *   → agent    agents without an assignment drop it
   *   → workflow every agent drops it; from then on it exists only for the
   *              workflow steps it's attached to
   */
  const setScope = useMutation({
    mutationFn: ({ skill, scope }: { skill: TenantSkill; scope: TenantSkillScope }) =>
      skillsApi.updateTenantSkill(tenantId, skill.id, { scope }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: queryKeys.tenantSkills.byCompany(tenantId) }),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Zap size={18} />
          Skills ({tenantSkills.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        {availableSkills.length > 0 && (
          <div className="mb-4 flex items-center gap-2">
            <select
              value={selectedSkill}
              onChange={(e) => setSelectedSkill(e.target.value)}
              className="flex-1 rounded-md border border-border bg-surface px-3 py-2 text-sm"
            >
              <option value="">Select Skill to assign…</option>
              {availableSkills.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.displayName || c.name} ({c.skillType})
                </option>
              ))}
            </select>
            <Button
              size="sm"
              disabled={!selectedSkill || assignSkill.isPending}
              onClick={() => assignSkill.mutate(selectedSkill)}
            >
              <Plus size={14} className="mr-1" />
              Assign
            </Button>
          </div>
        )}

        {tenantSkills.length === 0 ? (
          <p className="text-sm text-text-muted">No skills assigned to this tenant.</p>
        ) : (
          <div className="flex flex-col gap-1.5">
            {tenantSkills.map((skill) => {
              const meta = SCOPE_META[skill.scope] ?? SCOPE_META.agent;
              const ScopeIcon = meta.icon;
              return (
                <div
                  key={skill.id}
                  className="flex items-center justify-between rounded px-2 py-2 hover:bg-surface-hover"
                >
                  <div className="flex items-center gap-2">
                    <Zap size={14} className="text-accent" />
                    <span className="text-sm text-text-primary">{skill.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Three scopes is one too many for a click-to-cycle pill:
                        cycling would silently pass through a scope that fans
                        out a sync to every agent. Pick explicitly instead. */}
                    <div
                      className="relative inline-flex items-center gap-1 rounded-md border border-border px-1.5 py-0.5 text-[11px] text-text-secondary"
                      title={meta.title}
                    >
                      <ScopeIcon size={11} />
                      {meta.label}
                      <select
                        aria-label={`Scope for ${skill.name}`}
                        value={skill.scope}
                        disabled={setScope.isPending}
                        onChange={(e) =>
                          setScope.mutate({
                            skill,
                            scope: e.target.value as TenantSkillScope,
                          })
                        }
                        className="absolute inset-0 cursor-pointer opacity-0 disabled:cursor-default"
                      >
                        <option value="agent">agent — assigned agents only</option>
                        <option value="global">global — every agent</option>
                        <option value="workflow">
                          workflow only — no chat, per-step
                        </option>
                      </select>
                    </div>
                    <Badge variant={skill.enabled ? "success" : "default"}>
                      {skill.enabled ? "enabled" : "disabled"}
                    </Badge>
                    <Button size="sm" variant="ghost" onClick={() => removeSkill.mutate(skill.id)}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
